import React, { useState, useEffect } from 'react';
import { api } from '../lib/api';
import { authService } from '../lib/auth';
import { toast } from 'sonner';
import { 
  TrendingUp, Phone, CheckCircle, Youtube, FileWarning, 
  MessageSquare, FileText, Package, AlertTriangle, Clock,
  IndianRupee, Users, PlayCircle, ArrowRight, UserCheck, Building2, Sparkles, ClipboardList, Banknote,
  DatabaseZap, ShieldCheck, ShieldAlert, MessageCircle
} from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import PhoneNumberAlert from '../components/PhoneNumberAlert';

const Dashboard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const user = authService.getCurrentUser();
  const navigate = useNavigate();

  useEffect(() => {
    if (user?.role === 'admin') {
      fetchAdminDashboard();
    } else {
      fetchBasicStats();
    }
  }, [user?.role]);

  const fetchAdminDashboard = async () => {
    try {
      const response = await api.getAdminDashboard();
      setStats(response.data);
    } catch (error) {
      toast.error('Failed to fetch dashboard data');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchBasicStats = async () => {
    try {
      const response = await api.getStats();
      setStats(response.data);
    } catch (error) {
      toast.error('Failed to fetch statistics');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-slate-600 font-body">Loading dashboard...</div>
      </div>
    );
  }

  // Non-admin dashboard (basic stats)
  if (user?.role !== 'admin') {
    return <NonAdminDashboard user={user} />;
  }

  // Clickable Card Component
  const ClickableCard = ({ to, children, className = '' }) => (
    <Link to={to} className={`block hover:scale-[1.02] transition-transform cursor-pointer ${className}`}>
      {children}
    </Link>
  );

  // Admin Dashboard
  return (
    <div data-testid="admin-dashboard" className="max-w-[1400px] mx-auto space-y-6">
      <PhoneNumberAlert />
      
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Admin Dashboard</h1>
          <p className="text-slate-500 text-sm">Real-time overview • Click any card to view details</p>
        </div>
        <div className="flex items-center gap-4">
          {/* DB Backup Status Indicator */}
          {stats?.last_backup ? (
            <div 
              data-testid="backup-indicator"
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium ${
                stats.last_backup.is_recent 
                  ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' 
                  : 'bg-red-50 text-red-700 border border-red-200'
              }`}
              title={`Last backup: ${stats.last_backup.date} at ${stats.last_backup.time?.replace('-', ':')} IST (${stats.last_backup.size_mb} MB)`}
            >
              {stats.last_backup.is_recent ? <ShieldCheck size={14} /> : <ShieldAlert size={14} />}
              <span>Backup: {stats.last_backup.date}</span>
            </div>
          ) : (
            <div 
              data-testid="backup-indicator-none"
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-slate-100 text-slate-500 border border-slate-200"
            >
              <DatabaseZap size={14} />
              <span>No backup yet</span>
            </div>
          )}
          {/* Pending Approvals Badge */}
          {(stats?.pending_approvals || 0) > 0 && (
            <Link 
              to="/admin/users" 
              className="flex items-center gap-2 px-3 py-2 bg-amber-100 text-amber-700 rounded-lg hover:bg-amber-200 transition"
            >
              <UserCheck size={18} />
              <span className="font-medium">{stats.pending_approvals} Pending Approvals</span>
            </Link>
          )}
          <div className="text-right">
            <p className="text-xs text-slate-400">Last updated</p>
            <p className="text-sm font-medium text-slate-600">{new Date().toLocaleTimeString()}</p>
          </div>
        </div>
      </div>

      {/* Row 1 - Online Metrics */}
      <div>
        <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">Online</p>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          {/* Today's Sales */}
          <ClickableCard to="/sales/product-wise">
            <div className="bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-xl p-5 text-white h-full">
              <div className="flex items-center justify-between mb-3">
                <IndianRupee size={24} className="opacity-80" />
                <span className="text-xs bg-white/20 px-2 py-0.5 rounded-full">Today</span>
              </div>
              <p className="text-3xl font-bold">₹{(stats?.today_sales?.total_revenue || 0).toLocaleString()}</p>
              <p className="text-emerald-100 text-sm mt-1">{stats?.today_sales?.total_sales || 0} sales</p>
            </div>
          </ClickableCard>

          {/* Live Callers */}
          <ClickableCard to="/sales/leaderboard">
            <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-5 text-white h-full">
              <div className="flex items-center justify-between mb-3">
                <Phone size={24} className="opacity-80" />
                <span className="text-xs bg-white/20 px-2 py-0.5 rounded-full">Active</span>
              </div>
              <p className="text-3xl font-bold">{stats?.live_callers || 0}</p>
              <p className="text-blue-100 text-sm mt-1">Live Callers</p>
            </div>
          </ClickableCard>

          {/* PPTs Typed Today */}
          <ClickableCard to="/typing">
            <div className="bg-gradient-to-br from-violet-500 to-violet-600 rounded-xl p-5 text-white h-full">
              <div className="flex items-center justify-between mb-3">
                <FileText size={24} className="opacity-80" />
                <span className="text-xs bg-white/20 px-2 py-0.5 rounded-full">Today</span>
              </div>
              <p className="text-3xl font-bold">{stats?.ppts_typed_today || 0}</p>
              <p className="text-violet-100 text-sm mt-1">PPTs Typed</p>
            </div>
          </ClickableCard>

          {/* Complaint Resolution — TODAY only */}
          <ClickableCard to="/complaints/dashboard">
            <div className="bg-gradient-to-br from-amber-500 to-amber-600 rounded-xl p-5 text-white h-full">
              <div className="flex items-center justify-between mb-3">
                <MessageSquare size={24} className="opacity-80" />
                <span className="text-xs bg-white/20 px-2 py-0.5 rounded-full">Today</span>
              </div>
              <p className="text-3xl font-bold">{stats?.complaints?.resolution_rate || 0}%</p>
              <p className="text-amber-100 text-sm mt-1">
                Resolution Rate
                <span className="block text-xs opacity-80">
                  {stats?.complaints?.solved || 0} of {stats?.complaints?.total || 0} filed today
                </span>
              </p>
            </div>
          </ClickableCard>

          {/* Special Events */}
          <ClickableCard to="/special-events">
            <div className="bg-gradient-to-br from-rose-500 to-rose-600 rounded-xl p-5 text-white h-full">
              <div className="flex items-center justify-between mb-3">
                <Sparkles size={24} className="opacity-80" />
                <span className="text-xs bg-white/20 px-2 py-0.5 rounded-full">Today</span>
              </div>
              <p className="text-3xl font-bold">{stats?.special_events?.completed_today || 0}</p>
              <p className="text-rose-100 text-sm mt-1">Events Done</p>
            </div>
          </ClickableCard>

          {/* Doubt Engine — pending teacher replies */}
          <ClickableCard to="/doubt-engine">
            <div className="bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-xl p-5 text-white h-full" data-testid="admin-doubt-engine-card">
              <div className="flex items-center justify-between mb-3">
                <MessageCircle size={24} className="opacity-80" />
                <span className={`text-xs px-2 py-0.5 rounded-full ${
                  (stats?.doubt_engine?.pending_replies || 0) > 0 ? 'bg-red-500/90' : 'bg-white/20'
                }`}>
                  {(stats?.doubt_engine?.pending_replies || 0) > 0 ? 'Pending' : 'Live'}
                </span>
              </div>
              <p className="text-3xl font-bold" data-testid="admin-doubt-pending-count">
                {stats?.doubt_engine?.pending_replies || 0}
              </p>
              <p className="text-emerald-100 text-sm mt-1">
                Doubts Awaiting Reply
                <span className="block text-xs opacity-80">
                  {stats?.doubt_engine?.active_chats || 0} active chats
                </span>
              </p>
            </div>
          </ClickableCard>
        </div>
      </div>

      {/* Row 2 - Offline Metrics */}
      <div>
        <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">Offline Centre</p>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          {/* Offline Revenue */}
          <ClickableCard to="/offline/admissions">
            <div className="bg-gradient-to-br from-teal-500 to-teal-600 rounded-xl p-5 text-white h-full">
              <div className="flex items-center justify-between mb-3">
                <Building2 size={24} className="opacity-80" />
                <span className="text-xs bg-white/20 px-2 py-0.5 rounded-full">Offline</span>
              </div>
              <p className="text-3xl font-bold">₹{(stats?.offline_revenue?.today_collection || 0).toLocaleString()}</p>
              <p className="text-teal-100 text-sm mt-1">{stats?.offline_revenue?.transactions || 0} collections</p>
            </div>
          </ClickableCard>

          {/* Offline Enquiries */}
          <ClickableCard to="/offline/enquiries">
            <div className="bg-gradient-to-br from-cyan-500 to-cyan-600 rounded-xl p-5 text-white h-full">
              <div className="flex items-center justify-between mb-3">
                <ClipboardList size={24} className="opacity-80" />
                <span className="text-xs bg-white/20 px-2 py-0.5 rounded-full">Enquiries</span>
              </div>
              <p className="text-3xl font-bold">{stats?.offline_revenue?.today_enquiries || 0}</p>
              <p className="text-cyan-100 text-sm mt-1">Today ({stats?.offline_revenue?.active_enquiries || 0} active)</p>
            </div>
          </ClickableCard>

          {/* Cash to be Deposited */}
          <ClickableCard to="/offline/reports">
            <div className={`bg-gradient-to-br ${(stats?.pending_cash_deposit || 0) > 0 ? 'from-red-500 to-red-600' : 'from-slate-400 to-slate-500'} rounded-xl p-5 text-white h-full`}>
              <div className="flex items-center justify-between mb-3">
                <Banknote size={24} className="opacity-80" />
                <span className="text-xs bg-white/20 px-2 py-0.5 rounded-full">Cash</span>
              </div>
              <p className="text-3xl font-bold">₹{(stats?.pending_cash_deposit || 0).toLocaleString()}</p>
              <p className="text-red-100 text-sm mt-1">To be Deposited</p>
            </div>
          </ClickableCard>
        </div>
      </div>

      {/* Exampur Store Stats */}
      {stats?.store && (
        <div>
          <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">Exampur Store</p>
          <ClickableCard to="/ecommerce">
            <div className="bg-white rounded-xl border border-slate-200 p-5">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <p className="text-xs text-slate-400">Today's Orders</p>
                  <p className="text-2xl font-bold text-slate-900">{stats.store.orders_today || 0}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-400">COD / Prepaid</p>
                  <p className="text-2xl font-bold text-slate-900">{stats.store.cod_today || 0} / {stats.store.prepaid_today || 0}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-400">Today's Revenue</p>
                  <p className="text-2xl font-bold text-emerald-600">₹{(stats.store.revenue_today || 0).toLocaleString()}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-400">Total Revenue</p>
                  <p className="text-2xl font-bold text-slate-700">₹{(stats.store.total_revenue || 0).toLocaleString()}</p>
                </div>
              </div>
              {stats.store.top_products?.length > 0 && (
                <div className="mt-3 pt-3 border-t border-slate-100">
                  <p className="text-xs text-slate-400 mb-1">Top Selling</p>
                  <div className="flex gap-4">
                    {stats.store.top_products.slice(0, 3).map((p, i) => (
                      <span key={i} className="text-xs text-slate-600">{i + 1}. {p.title} ({p.order_count} orders)</span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </ClickableCard>
        </div>
      )}

      {/* Second Row - Classes & Alerts */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* App Classes Card */}
        <ClickableCard to="/classes/app">
          <div className="bg-white rounded-xl border border-slate-200 p-5 h-full hover:border-indigo-300 transition">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2.5 bg-indigo-100 rounded-lg">
                <PlayCircle size={20} className="text-indigo-600" />
              </div>
              <div>
                <h3 className="font-semibold text-slate-800">App Classes</h3>
                <p className="text-xs text-slate-500">Today's schedule</p>
              </div>
            </div>
            <div className="flex items-end justify-between">
              <div>
                <p className="text-3xl font-bold text-slate-800">
                  {stats?.app_classes?.completed || 0}
                  <span className="text-lg text-slate-400 font-normal">/{stats?.app_classes?.scheduled || 0}</span>
                </p>
                <p className="text-sm text-slate-500">Completed</p>
              </div>
              <div className="text-right">
                <p className={`text-2xl font-bold ${(stats?.app_classes?.completion_rate || 0) >= 80 ? 'text-emerald-500' : 'text-amber-500'}`}>
                  {stats?.app_classes?.completion_rate || 0}%
                </p>
                <p className="text-xs text-slate-400">Completion</p>
              </div>
            </div>
          </div>
        </ClickableCard>

        {/* YouTube Classes Card */}
        <ClickableCard to="/classes/youtube">
          <div className="bg-white rounded-xl border border-slate-200 p-5 h-full hover:border-red-300 transition">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2.5 bg-red-100 rounded-lg">
                <Youtube size={20} className="text-red-600" />
              </div>
              <div>
                <h3 className="font-semibold text-slate-800">YouTube Classes</h3>
                <p className="text-xs text-slate-500">Today's schedule</p>
              </div>
            </div>
            <div className="flex items-end justify-between">
              <div>
                <p className="text-3xl font-bold text-slate-800">
                  {stats?.youtube_classes?.completed || 0}
                  <span className="text-lg text-slate-400 font-normal">/{stats?.youtube_classes?.scheduled || 0}</span>
                </p>
                <p className="text-sm text-slate-500">Completed</p>
              </div>
              <div className="text-right">
                <p className={`text-2xl font-bold ${(stats?.youtube_classes?.completion_rate || 0) >= 80 ? 'text-emerald-500' : 'text-amber-500'}`}>
                  {stats?.youtube_classes?.completion_rate || 0}%
                </p>
                <p className="text-xs text-slate-400">Completion</p>
              </div>
            </div>
          </div>
        </ClickableCard>

        {/* Alerts Card */}
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2.5 bg-rose-100 rounded-lg">
              <AlertTriangle size={20} className="text-rose-600" />
            </div>
            <div>
              <h3 className="font-semibold text-slate-800">Attention Required</h3>
              <p className="text-xs text-slate-500">Action items</p>
            </div>
          </div>
          <div className="space-y-3">
            {/* Pending PDFs */}
            <Link to="/classes/app" className="flex items-center justify-between p-2 bg-amber-50 rounded-lg hover:bg-amber-100 transition">
              <div className="flex items-center gap-2">
                <FileWarning size={16} className="text-amber-600" />
                <span className="text-sm text-slate-700">PDFs Pending (&gt;4hrs)</span>
              </div>
              <span className={`font-bold ${(stats?.pending_pdf_classes?.count || 0) > 0 ? 'text-amber-600' : 'text-slate-400'}`}>
                {stats?.pending_pdf_classes?.count || 0}
              </span>
            </Link>
            {/* Overdue Complaints */}
            <Link to="/complaints" className="flex items-center justify-between p-2 bg-rose-50 rounded-lg hover:bg-rose-100 transition">
              <div className="flex items-center gap-2">
                <Clock size={16} className="text-rose-600" />
                <span className="text-sm text-slate-700">Overdue Complaints (&gt;2d)</span>
              </div>
              <span className={`font-bold ${(stats?.complaints?.overdue_count || 0) > 0 ? 'text-rose-600' : 'text-slate-400'}`}>
                {stats?.complaints?.overdue_count || 0}
              </span>
            </Link>
          </div>
        </div>
      </div>

      {/* Third Row - Pending PDF Classes */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Pending PDF Classes - Count Only */}
        <Link to="/classes/app" className="bg-white rounded-xl border border-slate-200 overflow-hidden hover:shadow-md transition cursor-pointer block">
          <div className="px-5 py-5 flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-xl bg-amber-50">
                <FileWarning size={24} className="text-amber-600" />
              </div>
              <div>
                <h3 className="font-semibold text-slate-800">Classes Missing PDFs</h3>
                <p className="text-xs text-slate-500 mt-0.5">Today's classes without attached PDFs</p>
              </div>
            </div>
            <div className="text-right">
              <p className={`text-3xl font-bold ${(stats?.pending_pdf_classes?.count || 0) > 0 ? 'text-amber-600' : 'text-emerald-500'}`}>
                {stats?.pending_pdf_classes?.count || 0}
              </p>
              <p className="text-xs text-slate-500 flex items-center gap-1 justify-end">View All <ArrowRight size={12} /></p>
            </div>
          </div>
        </Link>
      </div>
    </div>
  );
};

const NonAdminDashboard = ({ user }) => {
  const [myEvents, setMyEvents] = useState([]);
  const [loadingEvents, setLoadingEvents] = useState(false);
  const isTeacher = user?.role === 'teacher';

  useEffect(() => {
    if (isTeacher) {
      setLoadingEvents(true);
      api.getMySpecialEvents()
        .then(res => setMyEvents(res.data))
        .catch(() => {})
        .finally(() => setLoadingEvents(false));
    }
  }, [isTeacher]);

  const statusColors = {
    draft: 'bg-slate-100 text-slate-700',
    pending_review: 'bg-amber-100 text-amber-700',
    approved: 'bg-blue-100 text-blue-700',
    rejected: 'bg-red-100 text-red-700',
    completed: 'bg-green-100 text-green-700',
  };
  const statusLabels = {
    draft: 'Draft', pending_review: 'In Review', approved: 'Approved',
    rejected: 'Rejected', completed: 'Completed',
  };

  const activeEvents = myEvents.filter(e => e.status !== 'completed');
  const completedCount = myEvents.filter(e => e.status === 'completed').length;

  return (
    <div data-testid="main-dashboard" className="max-w-[1400px] mx-auto space-y-6">
      <PhoneNumberAlert />
      <div>
        <h1 className="text-2xl font-bold text-slate-900 mb-1">Welcome, {user?.name}!</h1>
        <p className="text-slate-600">Your dashboard overview</p>
      </div>

      {isTeacher && (
        <div className="space-y-4">
          {/* Stats Row */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            <div className="bg-white rounded-xl border p-5">
              <p className="text-sm text-slate-500">Total Events</p>
              <p className="text-2xl font-bold text-slate-900">{myEvents.length}</p>
            </div>
            <div className="bg-white rounded-xl border p-5">
              <p className="text-sm text-slate-500">Completed</p>
              <p className="text-2xl font-bold text-green-600">{completedCount}</p>
            </div>
            <div className="bg-white rounded-xl border p-5">
              <p className="text-sm text-slate-500">Active</p>
              <p className="text-2xl font-bold text-blue-600">{activeEvents.length}</p>
            </div>
          </div>

          {/* Active Events */}
          <div className="bg-white rounded-xl border">
            <div className="p-4 border-b">
              <h2 className="font-semibold text-slate-900">Your Special Events</h2>
            </div>
            {loadingEvents ? (
              <div className="p-8 text-center text-slate-400">Loading...</div>
            ) : activeEvents.length === 0 ? (
              <div className="p-8 text-center text-slate-400">No active events assigned</div>
            ) : (
              <div className="divide-y">
                {activeEvents.map(event => (
                  <div key={event.id} className="p-4 flex items-center gap-4 hover:bg-slate-50" data-testid={`my-event-${event.id}`}>
                    <div className="w-16 h-12 bg-slate-100 rounded overflow-hidden flex-shrink-0">
                      {event.thumbnail_url ? (
                        <img src={event.thumbnail_url} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <Sparkles size={16} className="text-slate-300" />
                        </div>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-slate-900 truncate">{event.title}</p>
                      <p className="text-xs text-slate-400">{event.event_date || 'No date set'}</p>
                    </div>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${statusColors[event.status]}`}>
                      {statusLabels[event.status]}
                    </span>
                    {event.youtube_link && (
                      <a href={event.youtube_link} target="_blank" rel="noopener noreferrer" className="text-red-500 hover:text-red-700">
                        <Youtube size={18} />
                      </a>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {!isTeacher && (
        <div className="bg-white rounded-xl border p-8 text-center text-slate-500">
          Contact admin for dashboard access
        </div>
      )}
    </div>
  );
};

export default Dashboard;
